import { useEffect } from "react";

import { AnsiHtml } from "fancy-ansi/react";
import { observer } from "mobx-react-lite";

import { disposeApi } from "@/api";
import { getMarimoFilesBaseUrl } from "@/api/marimo-api";
import { Spinner } from "@/components/spinner/Spinner";
import { twMerge } from "@/utils/twMerge";

import { FitIframe } from "./FitIframe";
import { LazyVegaEmbed } from "./LazyVegaEmbed";

import type { DocumentModel } from "@/models/DocumentModel";

function renderMarimoOutput(mimetype: string, data: unknown, baseHref: string) {
  if (mimetype.startsWith("image/")) {
    return <img alt="" src={data as string} className="max-w-full" />;
  }
  if (mimetype.startsWith("video/")) {
    return <video src={data as string} className="max-w-full" controls />;
  }
  if (mimetype.startsWith("application/vnd.vega")) {
    const vegaBundle = JSON.parse(data as string) as Record<string, unknown>;
    return (
      <LazyVegaEmbed
        spec={vegaBundle}
        options={{ actions: { compiled: false, editor: false, source: false } }}
      />
    );
  }
  if (mimetype === "text/html" || mimetype === "application/vnd.marimo+traceback") {
    return <FitIframe className="w-full" content={data as string} baseHref={baseHref} />;
  }

  const normalizedData = typeof data === "string" ? data : JSON.stringify(data);
  return <AnsiHtml text={normalizedData} />;
}

export const CodeExecutionResults = observer(function CodeExecutionResults({
  doc,
  cellId,
  outputTimestamp,
  className,
}: {
  doc: DocumentModel;
  cellId: string;
  outputTimestamp: string | null;
  className: string;
}) {
  useEffect(() => {
    return disposeApi(async (signal) => {
      const timestamp = outputTimestamp ? parseFloat(outputTimestamp) : null;
      await doc.marimo.loadCachedOutput(cellId, timestamp, signal);
    });
  }, [doc, cellId, outputTimestamp]);

  const cellResults = doc.marimo.getCellExecutionResults(cellId);
  const status = doc.marimo.cellStatuses.get(cellId);

  const isBusy = status === "queued" || status === "running";
  const isError = status === "error";

  const displayOutputs = cellResults?.displayOutputs ?? [];
  const consoleOutputs = cellResults?.consoleOutputs ?? [];
  const stale = cellResults?.stale;

  if (displayOutputs.length === 0 && consoleOutputs.length === 0 && !isBusy) {
    return null;
  }

  const baseHref = getMarimoFilesBaseUrl(doc.marimo.sessionId!);

  return (
    <ul
      aria-live="polite"
      aria-busy={isBusy}
      contentEditable={false}
      // tabIndex is required to allow content focus (e.g. for text selection),
      // otherwise cursor jumps to the nearest editable node
      tabIndex={-1}
      className={twMerge("m-0 p-0 list-none flex flex-col gap-2 w-full", className)}
    >
      {consoleOutputs.map((c, idx) => (
        <li
          // eslint-disable-next-line react/no-array-index-key
          key={`console-${idx}`}
          className={twMerge(
            "border px-5 py-3.5 rounded whitespace-pre-wrap text-size-13 wrap-anywhere",
            c.channel === "stderr"
              ? "border-red-400 dark:border-red-600"
              : "border-gray-200 dark:border-gray-800",
          )}
        >
          {renderMarimoOutput(c.mimetype, c.data, baseHref)}
        </li>
      ))}

      {displayOutputs.map((output, idx) => (
        <li
          // eslint-disable-next-line react/no-array-index-key
          key={`display-${idx}`}
          data-testid={`display-${idx}`}
          className={twMerge(
            "border px-5 py-3.5 rounded whitespace-pre-wrap text-size-13 wrap-anywhere",
            isError ? "border-red-400 dark:border-red-600" : "border-gray-200 dark:border-gray-800",
            stale && "bg-gray-100 dark:bg-gray-900",
          )}
        >
          {renderMarimoOutput(output.mimetype, output.data, baseHref)}
        </li>
      ))}

      {isBusy && (
        <li className="border border-gray-200 dark:border-gray-800 px-5 py-3.5 rounded">
          <Spinner />
        </li>
      )}
    </ul>
  );
});
